import dayjs from '~/timeInit';

import {
  HeatmapSurchargeApplication,
  HeatmapSurchargeApproval,
  HeatmapSurchargeBulkApprovalResult,
  HeatmapSurchargeCalendar,
} from '~/types/heatmapSurcharge';

export type HeatmapCalendarDateStatus = 'ready' | 'target_met' | 'pending' | 'applied' | 'needs_review';

/** Recheck the reviewed quote immediately before submitting a manual approval. */
export function assertReviewedQuoteIsCurrent(approval: HeatmapSurchargeApproval, latest: HeatmapSurchargeCalendar) {
  const issue = getReviewedQuoteIssue(approval, latest);
  if (issue) {
    throw new Error(issue.message || 'The forecast or pricing has changed.');
  }
}

function getReviewedQuoteIssue(
  approval: HeatmapSurchargeApproval,
  latest: HeatmapSurchargeCalendar,
): HeatmapSurchargeBulkApprovalResult | null {
  const quote = latest.surchargeQuotes.find((candidate) => candidate.travelDate === approval.quote.travelDate);
  if (quote?.approval) {
    return {
      travelDate: approval.quote.travelDate,
      status: 'skipped',
      reason: 'already_submitted',
      message: 'This surcharge was already submitted. Close this dialog to check its recorded outcome.',
      jobId: null,
      approval: quote.approval,
    };
  }
  if (quote?.status === 'already_meets_target') {
    return {
      travelDate: approval.quote.travelDate,
      status: 'skipped',
      reason: 'target_met',
      message: 'Current internal surcharge already meets the heatmap target.',
      jobId: null,
      approval: null,
    };
  }
  if (
    latest.offerId !== approval.offerId ||
    latest.selectedRoomTypeId !== approval.roomTypeId ||
    !dayjs(latest.generatedAt).isSame(dayjs(approval.generatedAt)) ||
    latest.pricingContext.status !== 'ready' ||
    quote?.roomRateId !== approval.quote.roomRateId ||
    quote?.suggestedSurchargePercentage !== approval.quote.suggestedSurchargePercentage ||
    quote?.baseNightlyPrice !== approval.quote.baseNightlyPrice ||
    quote?.vendorSurchargeTotal !== approval.quote.vendorSurchargeTotal ||
    quote?.currentInternalSurcharge !== approval.quote.currentInternalSurcharge ||
    quote?.currentNightlyPrice !== approval.quote.currentNightlyPrice ||
    quote?.targetInternalSurcharge !== approval.quote.targetInternalSurcharge ||
    quote?.additionalInternalSurcharge !== approval.quote.additionalInternalSurcharge ||
    quote?.proposedNightlyPrice !== approval.quote.proposedNightlyPrice
  ) {
    return {
      travelDate: approval.quote.travelDate,
      status: 'skipped',
      reason: 'invalid_pricing_snapshot',
      message: 'The forecast or pricing has changed. Close this dialog and review the refreshed recommendation.',
      jobId: null,
      approval: null,
    };
  }
  return null;
}

/** Keep only quotes whose reviewed price still matches, reporting every excluded date. */
export function partitionCurrentHeatmapApprovals(
  approvals: Array<HeatmapSurchargeApproval>,
  latest: HeatmapSurchargeCalendar,
) {
  const currentApprovals: Array<HeatmapSurchargeApproval> = [];
  const skippedResults: Array<HeatmapSurchargeBulkApprovalResult> = [];
  for (const approval of approvals) {
    const issue = getReviewedQuoteIssue(approval, latest);
    if (issue) {
      skippedResults.push(issue);
    } else {
      currentApprovals.push(approval);
    }
  }
  return { currentApprovals, skippedResults };
}

export function getApprovalKey(offerId: string, roomTypeId: string, travelDate: string) {
  return JSON.stringify([offerId, roomTypeId, travelDate]);
}

/** Resume pending jobs after navigation without creating duplicate status pollers. */
export function getApplicationJobs(
  calendar: HeatmapSurchargeCalendar | undefined,
  applications: Record<string, HeatmapSurchargeApplication>,
) {
  const jobs = Object.values(applications).map(({ jobId, label }) => ({ jobId, label }));
  if (!calendar) return jobs;

  const room = calendar.roomTypes.find((candidate) => candidate.roomTypeId === calendar.selectedRoomTypeId);
  for (const quote of calendar.surchargeQuotes) {
    const recordedApproval = quote.approval;
    if (
      recordedApproval?.status === 'pending' &&
      recordedApproval.jobId &&
      !jobs.some((job) => job.jobId === recordedApproval.jobId)
    ) {
      jobs.push({
        jobId: recordedApproval.jobId,
        label: `${room?.roomTypeName || calendar.selectedRoomTypeId} · ${dayjs(quote.travelDate).format(
          'D MMM YYYY',
        )} · ${calendar.offerId}`,
      });
    }
  }
  return jobs;
}

export function isApprovalBlocked(
  application: HeatmapSurchargeApplication | undefined,
  approval: HeatmapSurchargeApproval,
) {
  if (approval.quote.approval) return true;
  if (!application) return false;
  // A failed job can have partial effects; its outcome needs inspection before another attempt.
  return (
    application.status !== 'success' ||
    approval.quote.targetInternalSurcharge <= application.targetInternalSurcharge ||
    approval.quote.currentInternalSurcharge < application.targetInternalSurcharge
  );
}

/** Give each quoted date one status shared by the calendar badge and Review button. */
export function getHeatmapCalendarDateStatuses(
  calendar: HeatmapSurchargeCalendar,
  applications: Record<string, HeatmapSurchargeApplication>,
) {
  const statuses = new Map<string, HeatmapCalendarDateStatus>();
  for (const quote of calendar.surchargeQuotes) {
    if (quote.approval) {
      statuses.set(quote.travelDate, quote.approval.status);
      continue;
    }
    if (quote.status === 'already_meets_target') {
      statuses.set(quote.travelDate, 'target_met');
      continue;
    }

    const approval: HeatmapSurchargeApproval = {
      offerId: calendar.offerId,
      roomTypeId: calendar.selectedRoomTypeId,
      checkInMonth: calendar.selectedCheckInMonth,
      generatedAt: calendar.generatedAt,
      quote,
    };
    const application = applications[getApprovalKey(calendar.offerId, calendar.selectedRoomTypeId, quote.travelDate)];
    if (application && isApprovalBlocked(application, approval)) {
      statuses.set(
        quote.travelDate,
        application.status === 'pending' ? 'pending' : application.status === 'success' ? 'applied' : 'needs_review',
      );
      continue;
    }
    if (calendar.pricingContext.status === 'ready') statuses.set(quote.travelDate, 'ready');
  }
  return statuses;
}
